import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import type { JourneyPreferences } from '../api-types';
import { ConfirmDialog } from '../ConfirmDialog';
import type { JourneyAction } from './use-journey';

type Asking = 'stop' | 'resume' | { path: { code: string; label: string } } | null;

/** An ended journey can start again only when it ended for no clicks. */
export const canResume = (journey: JourneyPreferences) =>
  journey.state === 'stopped' || (journey.state === 'ended' && journey.endedReason === 'no_clicks');

/** The three choices of the profile's «Comunicazioni», each behind its confirmation. */
export function JourneyActions({
  journeys,
  busy,
  onAct,
}: {
  readonly journeys: readonly JourneyPreferences[];
  readonly busy: boolean;
  readonly onAct: (action: JourneyAction) => Promise<boolean>;
}) {
  const { t } = useTranslation('communications');
  const [asking, setAsking] = useState<Asking>(null);
  const [choosing, setChoosing] = useState(false);
  const choices = journeys.map((j) => ({
    code: j.journey,
    label: t(`journey.name.${j.journey}`, { defaultValue: j.journey }),
  }));
  const tap = 'min-h-11 @min-[620px]/shell:min-h-[38px]';

  const confirm = (action: JourneyAction) =>
    void onAct(action).then((done) => {
      // The dialog closes either way: a failure is said on the card, not behind the dialog.
      setAsking(null);
      if (done) setChoosing(false);
    });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-2">
        {journeys.some((j) => j.state === 'active') ? (
          <Button type="button" variant="outline" className={tap} onClick={() => setAsking('stop')}>
            {t('journey.stop')}
          </Button>
        ) : null}
        {journeys.some(canResume) ? (
          <Button type="button" className={tap} onClick={() => setAsking('resume')}>
            {t('journey.resume')}
          </Button>
        ) : null}
        {choices.length > 0 ? (
          <Button
            type="button"
            variant="outline"
            className={tap}
            aria-expanded={choosing}
            onClick={() => setChoosing((open) => !open)}
          >
            {t('journey.choose')}
          </Button>
        ) : null}
      </div>
      {choosing ? (
        <ul aria-label={t('journey.choose')} className="flex flex-col gap-2">
          {choices.map((choice) => (
            <li key={choice.code}>
              <Button
                type="button"
                variant="outline"
                className="min-h-11 w-full justify-start"
                onClick={() => setAsking({ path: choice })}
              >
                {choice.label}
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
      {asking === 'stop' ? (
        <ConfirmDialog
          title={t('journey.stopTitle')}
          ask={t('journey.stopAsk')}
          confirmLabel={t('journey.stop')}
          destructive
          busy={busy}
          onConfirm={() => confirm({ kind: 'stop' })}
          onClose={() => setAsking(null)}
        />
      ) : null}
      {asking === 'resume' ? (
        <ConfirmDialog
          title={t('journey.resumeTitle')}
          ask={t('journey.resumeAsk')}
          confirmLabel={t('journey.resume')}
          busy={busy}
          onConfirm={() => confirm({ kind: 'resume' })}
          onClose={() => setAsking(null)}
        />
      ) : null}
      {asking !== null && typeof asking === 'object' ? (
        <ConfirmDialog
          title={t('journey.chooseTitle')}
          ask={t('journey.chooseAsk', { label: asking.path.label })}
          confirmLabel={t('journey.chooseConfirm')}
          busy={busy}
          onConfirm={() => confirm({ kind: 'path', journey: asking.path.code })}
          onClose={() => setAsking(null)}
        />
      ) : null}
    </div>
  );
}
